/**
 * Money handling (AP-2, MONEY-1..MONEY-5).
 *
 * Every taka in this system passes through here. The rules:
 *   - money is never a JS `number` — IEEE-754 cannot represent 0.1 + 0.2
 *   - money crosses the API as a string, so JSON serialisation cannot corrupt it
 *   - allocations reconcile exactly: the parts always sum back to the total
 *
 * These are not stylistic preferences. Four partners will compare this ledger
 * against physical cash every week for the first three months (§14 item 5); a
 * balance that is off by one paisa is a balance nobody trusts.
 */

import Decimal from 'decimal.js';

/**
 * A local Decimal constructor so configuration cannot be changed out from under
 * us by any other library that also imports decimal.js.
 *
 * ROUND_HALF_UP matches how a person rounds money by hand, which is what the
 * partners will do when they check the numbers.
 */
export const Money = Decimal.clone({
  precision: 34,
  rounding: Decimal.ROUND_HALF_UP,
  toExpPos: 40,
  toExpNeg: -40,
});

export type MoneyValue = InstanceType<typeof Money>;

/** DECIMAL(14,2) — two decimal places, matching the database column (AP-2). */
export const MONEY_SCALE = 2;

/** DECIMAL(14,2) allows at most 12 integer digits. */
export const MONEY_MAX = new Money('999999999999.99');

/**
 * What `toMoney` accepts: a plain decimal number of any precision.
 *
 * Intermediate computations legitimately carry more than two decimal places —
 * an allocation share is 333.3333... before it is rounded — so the constructor
 * must accept them. Scientific notation, thousands separators and anything
 * non-numeric are refused because they indicate the value came from the wrong
 * place (a formatter, a locale-aware parser, or user input that was never
 * validated).
 */
const DECIMAL_INPUT_PATTERN = /^-?\d+(\.\d+)?$/;

/** What may be *stored or transmitted*: at most two decimal places (AP-2, MONEY-2). */
const MONEY_STORAGE_PATTERN = /^-?\d+(\.\d{1,2})?$/;

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyError';
  }
}

/**
 * Builds a Money value from a string, integer or existing Money.
 *
 * Plain `number` input is accepted only for integers: a non-integer number has
 * already lost precision before this function ever sees it, so accepting it
 * would silently launder a float into the ledger.
 */
export function toMoney(value: string | number | MoneyValue): MoneyValue {
  if (value instanceof Money) return value;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new MoneyError(`Not a finite amount: ${value}`);
    if (!Number.isInteger(value)) {
      throw new MoneyError(
        `Refusing to build money from the non-integer number ${value} — pass a string instead (MONEY-1). ` +
          'A float has already lost precision by the time it reaches this function.',
      );
    }
    return new Money(value);
  }

  const trimmed = value.trim();
  if (!DECIMAL_INPUT_PATTERN.test(trimmed)) {
    throw new MoneyError(
      `Not a valid amount: "${value}" — expected a plain decimal number ` +
        '(no currency symbol, thousands separator or scientific notation)',
    );
  }
  return new Money(trimmed);
}

/** Rounds to 2 decimal places, half-up. Applied at persistence and presentation only (MONEY-5). */
export function roundMoney(value: string | number | MoneyValue): MoneyValue {
  return toMoney(value).toDecimalPlaces(MONEY_SCALE, Decimal.ROUND_HALF_UP);
}

/** Canonical API representation: a fixed-2dp string (MONEY-2). */
export function toMoneyString(value: string | number | MoneyValue): string {
  return roundMoney(value).toFixed(MONEY_SCALE);
}

/**
 * True if the string is storable and transmittable as-is: at most two decimal
 * places and within DECIMAL(14,2) range.
 *
 * This is the API-boundary check. `toMoney` is deliberately more permissive
 * because computation needs precision that storage does not.
 */
export function isValidMoneyString(value: string): boolean {
  const trimmed = value.trim();
  if (!MONEY_STORAGE_PATTERN.test(trimmed)) return false;
  return new Money(trimmed).abs().lessThanOrEqualTo(MONEY_MAX);
}

/** Adds a list of amounts exactly. */
export function sumMoney(values: readonly (string | number | MoneyValue)[]): MoneyValue {
  return values.reduce<MoneyValue>((total, value) => total.plus(toMoney(value)), new Money(0));
}

/**
 * Splits `total` across `weights` in proportion, so that the parts sum back to
 * `total` exactly (MONEY-5, D-006).
 *
 * Used by trip cost allocation (BR-13 / P2-BR2), feed cost allocation
 * (BR-14 / P3-BR3), ownership percentages (BR-01), depreciation schedules
 * (BR-09) and loan installments.
 *
 * Each part is rounded to 2dp and the last recipient absorbs whatever rounding
 * remainder is left. Without this, ৳1,500 split three ways allocates ৳1,499.99
 * and per-animal costs stop reconciling to the ledger.
 *
 * Weights that are all zero fall back to an even split, which is the sensible
 * reading of "allocate this trip cost across animals that all cost nothing".
 */
export function allocateProportionally(
  total: string | number | MoneyValue,
  weights: readonly (string | number | MoneyValue)[],
): MoneyValue[] {
  if (weights.length === 0) throw new MoneyError('Cannot allocate across zero recipients');

  const totalAmount = roundMoney(total);
  const weightValues = weights.map((weight) => toMoney(weight));

  if (weightValues.some((weight) => weight.isNegative())) {
    throw new MoneyError('Allocation weights must not be negative');
  }

  const weightSum = weightValues.reduce<MoneyValue>((sum, weight) => sum.plus(weight), new Money(0));
  if (weightSum.isZero()) return allocateEvenly(totalAmount, weights.length);

  const parts: MoneyValue[] = [];
  let allocated = new Money(0);

  for (let index = 0; index < weightValues.length - 1; index += 1) {
    const weight = weightValues[index] as MoneyValue;
    const part = totalAmount.times(weight).dividedBy(weightSum).toDecimalPlaces(MONEY_SCALE, Decimal.ROUND_HALF_UP);
    parts.push(part);
    allocated = allocated.plus(part);
  }

  // The remainder, not a recomputed share — this is what makes the sum exact.
  parts.push(totalAmount.minus(allocated));
  return parts;
}

/** Splits `total` into `count` equal parts, the last absorbing the remainder (MONEY-5). */
export function allocateEvenly(total: string | number | MoneyValue, count: number): MoneyValue[] {
  if (!Number.isInteger(count) || count <= 0) {
    throw new MoneyError(`Recipient count must be a positive integer, got ${count}`);
  }

  const totalAmount = roundMoney(total);
  const share = totalAmount.dividedBy(count).toDecimalPlaces(MONEY_SCALE, Decimal.ROUND_HALF_UP);

  const parts = Array.from({ length: count - 1 }, () => share);
  const allocated = share.times(count - 1);
  parts.push(totalAmount.minus(allocated));
  return parts;
}

/**
 * Percentage shares that total exactly 100 (BR-01 ownership, BR-02 profit split).
 *
 * Returned to 2 decimal places with the remainder on the last share, so the
 * partners screen never shows four percentages adding up to 99.99.
 */
export function allocatePercentages(weights: readonly (string | number | MoneyValue)[]): MoneyValue[] {
  return allocateProportionally('100.00', weights);
}

/**
 * Formats an amount in the Bangladeshi lakh/crore grouping: the last three
 * digits, then groups of two (P0-TR5).
 *
 * ৳ 1,20,500 — not ৳ 120,500. Getting this wrong makes every figure in the app
 * read as unfamiliar to the people checking it.
 *
 * Digits stay in English per NFR-6 (open question Q3 in docs/DECISIONS.md).
 */
export function formatBDT(
  value: string | number | MoneyValue,
  options: { symbol?: boolean; decimals?: 'auto' | 'always' | 'never' } = {},
): string {
  const { symbol = true, decimals = 'auto' } = options;
  const amount = roundMoney(value);
  // Decimal keeps the sign of a value that rounds to zero, so -0.001 would
  // otherwise render as "-৳ 0". A ledger showing negative zero looks broken.
  const negative = amount.isNegative() && !amount.isZero();
  const absolute = amount.abs();

  const [integerPart = '0', fractionPart = '00'] = absolute.toFixed(MONEY_SCALE).split('.');

  const grouped = groupLakhStyle(integerPart);
  const showFraction = decimals === 'always' || (decimals === 'auto' && fractionPart !== '00');

  const body = showFraction ? `${grouped}.${fractionPart}` : grouped;
  return `${negative ? '-' : ''}${symbol ? '৳ ' : ''}${body}`;
}

/** 120500 → "1,20,500" · 600000 → "6,00,000" · 1234567 → "12,34,567" */
function groupLakhStyle(digits: string): string {
  if (digits.length <= 3) return digits;

  const lastThree = digits.slice(-3);
  const rest = digits.slice(0, -3);
  // Groups of two, right to left, above the final three digits.
  const groupedRest = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${groupedRest},${lastThree}`;
}

/** Parses a formatted string back to a Money value, tolerating ৳, spaces and commas. */
export function parseBDT(formatted: string): MoneyValue {
  const cleaned = formatted.replace(/[৳,\s]/g, '');
  if (cleaned === '') throw new MoneyError(`Cannot parse an empty amount from "${formatted}"`);
  return toMoney(cleaned);
}
